import React, { useEffect, useRef, useState } from 'react'
import { Button, Empty, Input, Spin, Tabs, Tree } from 'antd'
import { SearchOutlined, DeleteTwoTone, PlusOutlined, ShoppingTwoTone, SelectOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { CustomSettingWrapper, FormButton, List, ListHeader, ListWrapper, TreeWrapper, SearchWrapper, Section, SegmentLeft, SegmentRight, SubTitle, SectionWrapper } from '../segment.styled'
import { fetchApp, fetchCriteria, fetchInterest, fetchInvoiceTaxonomy, fetchProfile } from '../../../apis/segment'
import { fetchSources } from '../../../apis/source'
import { Sources } from '../../../constants/Sources'
import { useLocation } from 'react-router-dom'
import { fetchTaxonomyTree } from '../../../apis/taxonomy'
import { useSelector } from 'react-redux'
import { fetchInvoiceSegment } from '../../../apis/invoice'

const { SITE, TRACK, LINE, FIRST_PARTY, LOCATION, INTEREST, PROFILE, APP, TAXONOMY, GA4, INVOICE, INVOICE_TAXONOMY } = Sources
const useTree = [PROFILE, INTEREST, TAXONOMY, INVOICE_TAXONOMY]
function SegmentSetting (props) {
  const { goBack, setting, setSetting, sources, setSources, saveLoading, setShowModal, setModalData } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { nowGroup, nowBrand, groups } = user || {}
  const location = useLocation()
  const { state } = location || {}
  const { data, fromInvoice } = state || {}

  const endSectionRef = useRef()
  const [loading, setLoading] = useState(false)
  const [originalOptions, setOriginalOptions] = useState([])
  const [selectedTab, setSelectedTab] = useState()
  const [expandedKeys, setExpandedKeys] = useState([])
  const [searchValue, setSearchValue] = useState()
  const [checkedKeys, setCheckedKeys] = useState([])
  const [nowSection, setNowSection] = useState('1')
  const [sectionMode, setSectionMode] = useState({ 1: 'include' })
  const [preventLoop, setPreventLoop] = useState(false)
  const [treeData, setTreeData] = useState([])
  const [scrollToBottom, setScrollToBottom] = useState(false)

  const { profile, ...segments } = setting
  const noCriteria = Object.keys(segments)?.length === 0
  const getSourceId = name => sources?.find(i => i.name === name)?.id
  const defParams = { maxResults: 999, sort: '-updatedAt', brandId: nowBrand }
  const sourcesAPI = {
    [SITE]: () => fetchCriteria({ ...defParams, type: getSourceId(SITE) }),
    [TRACK]: () => fetchCriteria({ ...defParams, type: getSourceId(TRACK) }),
    [LINE]: () => fetchCriteria({ ...defParams, type: getSourceId(LINE) }),
    [FIRST_PARTY]: () => fetchCriteria({ ...defParams, type: getSourceId(FIRST_PARTY) }),
    [GA4]: () => fetchCriteria({ ...defParams, type: getSourceId(GA4) }),
    [LOCATION]: () => fetchCriteria({ ...defParams, type: getSourceId(LOCATION) }),
    [INTEREST]: () => fetchInterest({ ...defParams }),
    [PROFILE]: () => fetchProfile({ ...defParams }),
    [APP]: () => fetchApp({ ...defParams }).then(res => ({ ...res, data: res?.data?.map(i => ({ id: i.category, name: i.category })) })),
    [TAXONOMY]: () => fetchTaxonomyTree(nowGroup, { status: 1 }),
    [INVOICE]: () => fetchInvoiceSegment({ groupId: nowGroup, brandId: nowBrand }),
    [INVOICE_TAXONOMY]: () => fetchInvoiceTaxonomy({ ...defParams })
  }

  const setupCheckedKeys = (now = selectedTab || SITE) => {
    if (now?.toLowerCase() === 'profile') return setCheckedKeys(profile?.map(({ key }) => key))
    setCheckedKeys([
      ...setting[nowSection]?.include?.[now] || [],
      ...setting[nowSection]?.exclude?.[now] || []
    ]?.map(({ key }) => key))
  }

  const changeTab = tab => {
    setSelectedTab(tab)
    setExpandedKeys([])
    setSearchValue('')
    setupCheckedKeys(tab)
    if (tab !== 'Profile' && nowSection !== 'profile') return
    if (tab === 'Profile' && nowSection !== 'profile') {
      setPreventLoop(true)
      return setNowSection('profile')
    }
    setPreventLoop(true)
    setNowSection(`${Math.min(...Object.keys(segments))}`)
  }

  const handleCheck = (e, { checkedNodes }) => {
    setCheckedKeys(e)
    const checked = checkedNodes?.filter(({ disabled }) => !disabled)
    if (selectedTab === 'Profile') {
      setSetting({ ...setting, profile: checked })
      return setNowSection('profile')
    }
    setSetting({
      ...setting,
      [nowSection]: {
        ...setting[nowSection],
        [sectionMode[nowSection]]: { ...setting[nowSection][sectionMode[nowSection]], [selectedTab]: checked }
      }
    })
  }

  const handleProfileClicked = () => {
    setNowSection('profile')
    if (selectedTab !== 'Profile') changeTab('Profile')
  }

  const search = (name = '', value = '', children = []) => {
    if (name?.toLowerCase().includes(value?.toLowerCase())) return true
    if (children?.length === 0) return false
    const findInChild = children?.map(({ title, children }) => search(title, value, children))
    return findInChild.some(i => i === true)
  }
  const showDetail = data => {
    const dataSourceType = sources.find(i => i.id === data.type)
    setModalData({ ...data, dataSourceType })
    setShowModal(true)
  }

  const constructTag = ({ id, name, children: child = [] }, isDisable) => {
    const key = `${id}-${name}`
    const children = child?.map(i => constructTag(i, isDisable))
    return { key, title: name, parent: { name: TAXONOMY }, children, end: !child.length, disabled: isDisable(key), checked: true, type: TAXONOMY, name, id }
  }
  const constructTree = (type, title, data, key, parent, isDisable, id) => {
    const { children: child = [], ...other } = data || {}
    const children = [
      ...child?.map(({ code, name }) => constructTree(type, name, {}, `${key}-${code}`, { allId: [...parent?.allId || [], key] || [], name: title }, isDisable, code)),
      ...Object.keys(other).map(name => constructTree(type, name, other[name], `${key}-${name}`, { allId: [...parent?.allId || [], key] || [], name: title }, isDisable))
    ]
    const end = !child || child?.length === 0
    return { key, title, children, parent, end, disabled: isDisable(key), checked: true, type, id, name: title }
  }
  const constructProfile = (title, data, key, parent, isDisable, id) => {
    const children = data?.map(({ code, name }) => constructProfile(name, [], `${key}-${code}`, { allId: [...parent?.allId || [], key] || [], name: title }, isDisable, `${id}_${code}`))
    return { key, title, children, parent, end: !data || !data.length, disabled: isDisable(key), checked: true, type: PROFILE, id, name: title }
  }
  const getTree = () => {
    if (preventLoop) return setPreventLoop(false)
    if (!originalOptions) setTreeData([])
    const reverseMode = sectionMode[nowSection] === 'include' ? 'exclude' : 'include'
    const disableKeyList = setting?.[nowSection]?.[reverseMode]?.[selectedTab]?.map(({ key }) => key) || []
    const isDisable = key => nowSection !== 'Profile' && disableKeyList.includes(key)
    const data = !useTree.includes(selectedTab)
      ? originalOptions?.map(i => ({
        key: `0-${selectedTab}-${i.id}`,
        title: <>
          <span style={{ cursor: 'default' }}>{i.name}</span>
          {i.rule ? <SelectOutlined className="selectIcon" onClick={() => showDetail(i)} /> : ''}
        </>,
        name: i.name,
        children: [],
        parent: { name: selectedTab },
        end: true,
        disabled: isDisable(`0-${selectedTab}-${i.id}`),
        checked: true,
        id: i.id,
        type: selectedTab
      }))
      : selectedTab === PROFILE
        ? Object.keys(originalOptions || {})?.map(key => {
          const name = (key.charAt(0).toUpperCase() + key.slice(1)).replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          return constructProfile(name, originalOptions[key], `0-${selectedTab}-${name}`, undefined, isDisable, key)
        })
        : selectedTab === INTEREST || selectedTab === INVOICE_TAXONOMY
          ? Object.keys(originalOptions || {})?.map(key => constructTree(selectedTab, key, originalOptions[key], `0-${selectedTab}-${key}`, undefined, isDisable))
          : originalOptions?.map(i => constructTag(i, isDisable))

    if (!searchValue || searchValue === '') setTreeData(data)
    const included = []
    data.map(i => search(i.name, searchValue, i.children) ? included.push(i) : '')
    const expanded = []
    const getKey = ({ key, children }) => { expanded.push(key); return children }
    let child = included?.map(i => getKey(i)).flat()
    while (child && child.length > 0) { child = child?.map(i => getKey(i)).flat().filter(i => i) }
    setExpandedKeys(expanded)
    setTreeData(included)
  }
  useEffect(() => getTree(), [originalOptions, nowSection, sectionMode])
  useEffect(() => { if (searchValue) getTree() }, [searchValue])

  const fetchSourceOption = async type => {
    setLoading(true)
    const res = await sourcesAPI[type]()
    if (res?.status === 200) setOriginalOptions(res?.data || [])
    else setOriginalOptions([])
    setTimeout(() => setLoading(false), 300)
  }

  const initFetch = async () => {
    const source = await fetchSources({ maxResults: 999 })
    if (source?.status === 200) {
      const availableSrc = groups?.find(i => i.id === nowGroup)?.dataSourceTypes?.split(',') || []
      const sourcesWithTranslations = source.data?.filter(i => availableSrc.includes(`${i.id}`))
        .map(item => ({ ...item, displayName: ln_t(`segment.${item.name}`) }))
      setSources(sourcesWithTranslations)
      setSelectedTab(noCriteria ? 'Profile' : sourcesWithTranslations?.[0]?.name)
    }
  }

  useEffect(() => {
    initFetch()
    if (!data || fromInvoice) return
    const { UISetting } = data
    const { profile, ...segments } = UISetting || {}
    setSectionMode(Object.keys(segments).reduce((all, val) => ({ ...all, [val]: 'include' }), {}))
  }, [])
  useEffect(() => { initFetch() }, [user.language])
  useEffect(() => { setupCheckedKeys() }, [nowSection])
  useEffect(() => { if (selectedTab) fetchSourceOption(selectedTab) }, [selectedTab])

  useEffect(() => {
    if (scrollToBottom) {
      endSectionRef?.current?.scrollIntoView()
      setScrollToBottom(false)
    }
  }, [scrollToBottom])

  const settingComponent = ({ key, name, end, parent }, section, type) => {
    const handleDelItem = () => {
      const exclude = checkKey => checkKey !== key && !parent?.allId?.includes(checkKey)
      setCheckedKeys([...checkedKeys]?.filter(i => exclude(i)))
      if (section === 'profile') return setSetting({ ...setting, profile: [...setting.profile].filter(i => exclude(i.key)) })
      const mode = sectionMode[section]
      const original = [...setting[section]?.[mode]?.[type]]
      setSetting({
        ...setting,
        [section]: { ...setting[section], [mode]: { ...setting[section][mode], [type]: original.filter(i => exclude(i.key)) } }
      })
    }
    return end
      ? (
        <List key={key}>
          <div className="txt">{parent?.name} &gt; {name}</div>
          <div className="btn" onClick={handleDelItem}><DeleteTwoTone twoToneColor="#707070" /></div>
        </List>
        )
      : ''
  }
  const tabIcon = { bought: <ShoppingTwoTone style={{ marginRight: '0px' }} twoToneColor="#47b6af" /> }
  return (
    <CustomSettingWrapper>
      <SubTitle style={{ paddingBottom: '10px' }}>{ln_t('segment.segment')} {ln_t('util.setting')}</SubTitle>
      <Tabs
        items={sources?.filter(i => !noCriteria || i.name === 'Profile')?.map(i => ({ key: i.name, label: <>{tabIcon[i.type] || ''} {i.displayName}</> }))}
        activeKey={selectedTab}
        onChange={e => changeTab(e)}
      />
      <div className="d-flex">
        <SegmentLeft>
          <SearchWrapper>
            <Input.Search
              allowClear
              value={searchValue}
              onChange={e => setSearchValue(e?.target?.value)}
              style={{ width: '100%' }}
              placeholder={ln_t('util.search')}
              enterButton={<Button type="primary" size="small" icon={<SearchOutlined />} />}
              onKeyPress={e => { if (e.key === 'Enter') e.preventDefault() }} // to prevent submitting form on enter
            />
          </SearchWrapper>
          {loading
            ? <Spin className="d-flex justify-content-center mt-5" />
            : treeData && treeData?.length
              ? <TreeWrapper>
                  <Tree
                    checkable
                    className={useTree.includes(selectedTab) ? '' : 'noExpand'}
                    treeData={treeData}
                    expandedKeys={expandedKeys}
                    onExpand={e => setExpandedKeys(e)}
                    onCheck={(e, data) => handleCheck(e, data)}
                    checkedKeys={checkedKeys}
                  />
                </TreeWrapper>
              : <Empty className="mt-5" />
          }
        </SegmentLeft>

        <SegmentRight>
          <SectionWrapper>
            <SubTitle section>{ln_t('segment.profile')}</SubTitle>
            <Section active={nowSection === 'profile'} onClick={handleProfileClicked}>
              <ListWrapper>{profile?.map(i => settingComponent(i, 'profile'))}</ListWrapper>
            </Section>
            {Object.keys(segments)?.map((key, idx) => {
              const sectionActive = nowSection === key
              const list = segments[key]?.[sectionMode[key]] || {}
              const handleDelete = () => {
                const temp = { ...setting }
                delete temp[key]
                setSetting(temp)
                if (!Object.keys(temp).some(i => i !== 'profile')) handleProfileClicked()
              }
              return (
                <div key={key} ref={idx === Object.keys(segments).length - 1 ? endSectionRef : undefined}>
                  <SubTitle section>
                    {ln_t('segment.condition')} #{key}
                    <DeleteTwoTone style={{ marginLeft: '8px', cursor: 'pointer' }} onClick={handleDelete} twoToneColor="#707070" />
                  </SubTitle>
                  <Section active={sectionActive} onClick={() => setNowSection(key)}>
                    <div className="d-flex">
                      {['include', 'exclude'].map(mode => (
                        <ListHeader key={mode} active={sectionMode[key] === mode && sectionActive} onClick={() => setSectionMode({ ...sectionMode, [key]: mode })}>
                          {ln_t(`segment.${mode}`)}
                        </ListHeader>
                      ))}
                    </div>
                    <ListWrapper withHeader>
                      {Object.keys(list)?.map(type => list[type]?.map(i => settingComponent(i, key, type)))}
                    </ListWrapper>
                  </Section>
                </div>
              )
            })}
          </SectionWrapper>
          <Button type="default" className="add-btn" icon={<PlusOutlined />} onClick={() => {
            const nowCount = Math.max(...Object.keys(segments).map(i => Number(i)))
            const num = (isFinite(nowCount) ? nowCount : 0) + 1
            setSetting({ ...setting, [num]: { include: {}, exclude: {} } })
            setSectionMode({ ...sectionMode, [num]: 'include' })
            setNowSection(`${num}`)
            setScrollToBottom(true)
          }}>
            {ln_t('util.add')} {ln_t('segment.condition')}
          </Button>
          <FormButton>
            <Button type="default" onClick={goBack}>{ln_t('util.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={saveLoading}>{ln_t('util.confirm')}</Button>
          </FormButton>
        </SegmentRight>
      </div>

    </CustomSettingWrapper>
  )
}

export default SegmentSetting
